import { ethers, Contract, Signer, providers, utils } from "ethers";
import chainConfig from "../../util/chainConfig";
import Big from "big.js";
import { getQuoteInfo, setQuote } from "../../util/routerController";
import { getIcon } from "../../util/index";
import {
  QuoteRequest,
  QuoteResponse,
  ExecuteRequest,
  StatusParams,
  Token
} from "../../type/index";
import { FeeType } from "../../type/index";
import { Chain, createWalletClient, custom } from "viem";
import { http } from "viem";
import { approve } from "../../util/approve";
import weth from "@/configs/contract/weth";
import getWrapOrUnwrapTx from "@/sections/swap/getWrapOrUnwrapTx";
import quoter from "@/sdk/smart-router";
import { usePriceStore } from "@/stores/usePriceStore";

const nativeAddress = "0x0000000000000000000000000000000000000000";

function processPriceImpact(priceImpact: string | number | null): { priceImpact: string | null; priceImpactType: number } {
  if (!priceImpact) {
    return { priceImpact: null, priceImpactType: 0 };
  }

  const priceImpactValue = Big(priceImpact || 0);
  const absPriceImpact = priceImpactValue.abs();
  let priceImpactType = 0;

  if (absPriceImpact.gt(1)) {
    priceImpactType = 1;
  }
  if (absPriceImpact.gt(5)) {
    priceImpactType = 2;
  }
  if (absPriceImpact.gt(15)) {
    priceImpactType = 3;
  }

  let finalPriceImpact: string;
  if (absPriceImpact.gt(100)) {
    finalPriceImpact = priceImpactType > 0 ? "100" : "-100";
  } else {
    finalPriceImpact = priceImpactValue.toFixed(4);
  }

  return {
    priceImpact: new Big(finalPriceImpact || 0).toFixed(4),
    priceImpactType
  };
}

export async function getQuote(
  quoteRequest: QuoteRequest,
  signer: Signer
): Promise<QuoteResponse[] | null> {
  const numFromChainId = Number(quoteRequest.fromChainId);
  const numToChainId = Number(quoteRequest.toChainId);
  const prices = usePriceStore.getState().price;

  if (!chainConfig[numFromChainId] || !chainConfig[numToChainId]) {
    return null;
  }

  if (quoteRequest.fromToken.address === "native") {
    quoteRequest.fromToken.address = nativeAddress;
  }

  if (quoteRequest.toToken.address === "native") {
    quoteRequest.toToken.address = nativeAddress;
  }

  const wethAddress = weth[quoteRequest.fromChainId as any];

  const wrapType =
    quoteRequest.fromToken.address.toLowerCase() ===
      "0x0000000000000000000000000000000000000000" &&
      quoteRequest.toToken.address.toLowerCase() === wethAddress.toLowerCase()
      ? 1
      : quoteRequest.fromToken.address.toLowerCase() ===
        wethAddress.toLowerCase() &&
        quoteRequest.toToken.address.toLowerCase() ===
        "0x0000000000000000000000000000000000000000"
        ? 2
        : 0;

  const routes: any = [];

  if (wrapType) {
    await createRoute(null, routes, quoteRequest, wrapType, signer);
    if (routes.length > 0) {
      return routes;
    } else {
      return null;
    }
  }

  const routesRequest: any = {
    protocols: "v2,v3,mixed",
    tokenInAddress: quoteRequest.fromToken.address,
    tokenInChainId: numFromChainId,
    tokenOutAddress: quoteRequest.toToken.address,
    tokenOutChainId: numToChainId,
    amount: quoteRequest.amount.toFixed(0),
    type: "exactIn",
    recipient: quoteRequest.destAddress,
    slippageTolerance: Number(quoteRequest.slippage || 0.5).toFixed(2)
  };

  const queryParams = new URLSearchParams({
    protocols: routesRequest.protocols,
    tokenInAddress: routesRequest.tokenInAddress,
    tokenInChainId: routesRequest.tokenInChainId.toString(),
    tokenOutAddress: routesRequest.tokenOutAddress,
    tokenOutChainId: routesRequest.tokenOutChainId.toString(),
    amount: routesRequest.amount,
    type: routesRequest.type,
    recipient: routesRequest.recipient,
    slippageTolerance: routesRequest.slippageTolerance.toString() || "0.5",
    providers: 'Kodiak,OpenOcean,Magpie,Enso,Kyberswap',
    // refCode: 'S5GSR6OV',
    refCode: "4",
    referrerFeeBps: "7"
  });

  const response = await fetch(
    `https://backend.kodiak.finance/quote?${queryParams}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Kodiak API request failed: ${response.status}`);
  }

  const result = await response.json();

  const processedPriceImpact = processPriceImpact(result.priceImpact);
  result.priceImpact = processedPriceImpact.priceImpact;
  result.priceImpactType = processedPriceImpact.priceImpactType;

  await createRoute(result, routes, quoteRequest, wrapType, signer);
  if (result.otherQuote && result.provider !== result.otherQuote.provider) {
    if (result.otherQuote.provider === "Kodiak") {
      let isSuccess = false;
      try {
        const contractParams = {
          inputCurrency: {
            ...quoteRequest.fromToken,
            chainId: quoteRequest.fromChainId,
            isNative: quoteRequest.fromToken.symbol.toLowerCase() === "bera"
          },
          outputCurrency: {
            ...quoteRequest.toToken,
            chainId: quoteRequest.toChainId,
            isNative: quoteRequest.toToken.symbol.toLowerCase() === "bera"
          },
          inputAmount: quoteRequest.amount
            .div(10 ** quoteRequest.fromToken.decimals)
            .toString(),
          slippage: Number(routesRequest.slippageTolerance || 0.5) / 100,
          account: await signer.getAddress(),
          template: "Kodiak"
        };

        const contractData = await quoter(contractParams);

        if (contractData && contractData.txn && contractData.txn.data) {
          result.otherQuote.methodParameters = {
            calldata: contractData.txn.data,
            to: contractData.txn.to,
            value: contractData.txn.value
          };
          result.otherQuote.quote = Big(contractData.outputCurrencyAmount).mul(10 ** quoteRequest.toToken.decimals).toFixed(0);

          const { routes } = contractData
          const path: string[] = []
          if (routes?.length) {
            const innerRouutes = routes[0].routes
            if (innerRouutes?.length) {
              path.push(innerRouutes[0].token0.symbol)
            }
            for (const innerRoute of innerRouutes) {
              const token1 = innerRoute.token1
              path.push(token1.symbol)
            }
          }

          result.otherQuote.path = path;

          const inputCurrencyPrice =
            prices[quoteRequest.fromToken.symbol] || 0;
          const outputCurrencyPrice =
            prices[quoteRequest.toToken.symbol] || 0;
          let priceImpact = null;
          let priceImpactType = 0;

          if (inputCurrencyPrice && outputCurrencyPrice) {
            const usdBefore = quoteRequest.amount.div(10 ** quoteRequest.fromToken.decimals).mul(inputCurrencyPrice)
            const calculatedPriceImpact = new Big(contractData.outputCurrencyAmount).mul(outputCurrencyPrice)
              .minus(usdBefore)
              .div(usdBefore)
              .mul(100)
              .toFixed(4);

            const processedPriceImpact = processPriceImpact(calculatedPriceImpact);
            priceImpact = processedPriceImpact.priceImpact;
            priceImpactType = processedPriceImpact.priceImpactType;
          }

          result.otherQuote.priceImpact = priceImpact;
          result.otherQuote.priceImpactType = priceImpactType;

          result.priceImpact = new Big(result.priceImpact || 0);
          isSuccess = true;
        }

      } catch (error) {
        console.log('error:', error)
      }

      if (isSuccess) {
        await createRoute(
          result.otherQuote,
          routes,
          quoteRequest,
          wrapType,
          signer
        );
      }
    }

  }

  return routes.length > 0 ? routes : null;
}

export async function execute(
  request: ExecuteRequest,
  signer: Signer
): Promise<string | null> {
  const { route, isNative, amount, fromToken, isWrap } = getQuoteInfo(
    request.uuid
  );

  const account = await signer.getAddress();
  let transactionResponse: any;

  if (!isNative) {
    const isApprove = await approve(
      fromToken.address,
      amount,
      route.to,
      signer
    );
    if (!isApprove) {
      return null;
    }
  }

  if (isWrap) {
    transactionResponse = await signer.sendTransaction({
      ...route,
      from: account
    });
  } else {
    transactionResponse = await signer.sendTransaction({
      from: account,
      to: route.to,
      data: route.calldata,
      value: route.value
    });
  }

  await transactionResponse.wait();

  return transactionResponse.hash ? transactionResponse.hash : null;
}

export async function getStatus(params: StatusParams) {
  return {
    status: 1
  };
}

function computeFee(result: any, fromToken: Token, toToken: Token) {
  if (result.refFee) {
    return new Big(result.refFee).div(10 ** toToken.decimals).toString();
  }
  return "0";
}

async function createRoute(
  result: any,
  routes: any,
  quoteRequest: QuoteRequest,
  wrapType: number,
  signer: Signer
) {
  if (result && result.methodParameters) {
    const uuid = setQuote({
      route: {
        ...result.methodParameters
      },
      fromToken: quoteRequest.fromToken,
      toToken: quoteRequest.toToken,
      amount: quoteRequest.amount,
      isNative: quoteRequest.fromToken.symbol.toLowerCase() === "bera",
      bridgeType: "Kodiak",
      isWrap: false
    });

    let icon = "";
    if (result.provider.toLowerCase() === "kodiak") {
      icon = "/images/dapps/kodiak.svg";
    } else {
      icon = "/images/dapps/kx.png";
    }

    const route: any = {
      uuid,
      icon,
      bridgeName: result.provider,
      bridgeType: "Kodiak",
      fee: computeFee(result, quoteRequest.fromToken, quoteRequest.toToken),
      receiveAmount: result.quote,
      gas: result.gasUseEstimateUSD,
      duration: 1,
      feeType: FeeType.target,
      gasType: FeeType.origin,
      priceImpact: result.priceImpact,
      priceImpactType: result.priceImpactType || 0,
      path: result.path || null,
      identification: quoteRequest.identification,
      toexchangeRate: new Big(result.quoteDecimals)
        .div(quoteRequest.amount.div(10 ** quoteRequest.fromToken.decimals))
        .toString()
    };

    routes.push(route);
  } else {
    const { txn, gasLimit } = await getWrapOrUnwrapTx({
      signer,
      wethAddress: weth[quoteRequest.fromChainId as any],
      type: wrapType,
      amount: quoteRequest.amount.toFixed(0)
    });

    const uuid = setQuote({
      route: {
        ...txn,
        gasLimit: gasLimit ? gasLimit.toString() : undefined
      },
      fromToken: quoteRequest.fromToken,
      toToken: quoteRequest.toToken,
      amount: quoteRequest.amount,
      isNative: true,
      bridgeType: "Kodiak",
      isWrap: true
    });

    const route: any = {
      uuid,
      icon: "/images/dapps/kodiak.svg",
      bridgeName: "Kodiak",
      bridgeType: "Kodiak",
      fee: "0",
      receiveAmount: quoteRequest.amount,
      gas: "0",
      duration: 1,
      feeType: FeeType.target,
      gasType: FeeType.origin,
      priceImpact: 0,
      identification: quoteRequest.identification,
      toexchangeRate: "1"
    };

    routes.push(route);
  }
}
